// src/lib/ga4.ts
//
// Google Analytics 4, for one reason only: Raptive and Mediavine verify a
// site's traffic through a GA4 property. Supabase app_events stays the
// product analytics store; GA4 gets page views and nothing else.
//
// Consent Mode v2 defaults are pushed BEFORE the tag loads: granted
// everywhere, then denied for the EEA + UK + CH regions until Mediavine's
// CMP (Consent Mode V2 is enabled in their dashboard) pushes the `consent
// update` that grants them. The consent defaults themselves are NOT gated
// on VITE_GA4_ID: the Mediavine wrapper is static in the shell on every
// page, so its CMP is always a possibility and the dataLayer/gtag shim and
// the defaults have to exist for that update to land on something. Only
// the gtag.js tag itself (and its page_view suppression) requires an id.

import { analyticsPagePath } from './analyticsUrl'
export const GA4_ID: string = import.meta.env.VITE_GA4_ID ?? ''

// ISO 3166-1 alpha-2: EEA (EU 27 + IS, LI, NO) plus UK and Switzerland.
export const CONSENT_REGIONS = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU',
  'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES',
  'SE', 'IS', 'LI', 'NO', 'GB', 'CH',
] as const

type GtagWindow = Window & {
  dataLayer?: unknown[]
  gtag?: (...args: unknown[]) => void
  // Set by the inline bootstrap in index.html's head once IT has pushed the
  // two consent defaults, so this module does not push them a second time.
  __cbConsentDefaults?: number
}

/** The marker the index.html bootstrap sets after pushing the defaults. */
export const CONSENT_DEFAULTS_MARKER = '__cbConsentDefaults'

let initialised = false
let consentInitialised = false

export function resetGA4ForTests(): void {
  initialised = false
  consentInitialised = false
}

/**
 * Creates the dataLayer/gtag shim (if absent) and pushes the two Consent
 * Mode v2 defaults. Idempotent. This runs on every init, GA4 id or not,
 * because the consent signal that reads and updates these defaults comes
 * from Mediavine's CMP (static wrapper, every page), not from gtag.js.
 *
 * In a real page load the inline bootstrap in index.html's head has ALREADY
 * pushed both defaults, before the wrapper tag, because the wrapper's CMP
 * can fire its `consent update` before this module runs. The bootstrap
 * leaves a marker and this skips the pushes when it sees one, so a default
 * is never pushed AFTER an update and silently reverts it. The shim is
 * still created here for callers that arrive without the bootstrap (tests,
 * or a shell that lost the head script).
 */
function initConsentDefaults(win: Window): void {
  if (consentInitialised) return
  consentInitialised = true
  const w = win as GtagWindow
  w.dataLayer = w.dataLayer || []
  w.gtag = w.gtag || function gtag() {
    // eslint-disable-next-line prefer-rest-params
    w.dataLayer!.push(arguments)
  }
  if (w[CONSENT_DEFAULTS_MARKER]) return
  w.gtag('consent', 'default', {
    ad_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted',
    analytics_storage: 'granted',
  })
  w.gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    region: [...CONSENT_REGIONS],
    wait_for_update: 500,
  })
}

export function initGA4(
  id: string = GA4_ID,
  doc: Document = document,
  win: Window = window,
): boolean {
  if (initialised) return true
  initConsentDefaults(win)
  if (!id) return false
  initialised = true
  const w = win as GtagWindow
  w.gtag!('js', new Date())
  w.gtag!('config', id, { send_page_view: false, anonymize_ip: true })
  const s = doc.createElement('script')
  s.async = true
  s.dataset.ga4 = '1'
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`
  doc.head.appendChild(s)
  return true
}

/** The page_path GA4 receives for a location (query dropped on /rules and
 *  /collection; the list lives in analyticsUrl.ts, shared with Vercel). */
export function ga4PagePath(pathname: string, search: string): string {
  return analyticsPagePath(pathname, search)
}

export function trackGA4PageView(path: string, title: string, win: Window = window): void {
  if (!initialised) return
  const w = win as GtagWindow
  w.gtag?.('event', 'page_view', {
    page_path: path,
    page_title: title,
    page_location: `${w.location.origin}${path}`,
  })
}
